// THE IMPORT PATH IS LOAD-BEARING, and it is the reason this file exists rather
// than the duplication it removes.
//
// `@clerk/clerk-react/errors` re-exports `isClerkAPIResponseError` from
// `@clerk/shared/error` — measured, they are the SAME function object
// (`a === b` is true), so at runtime either import behaves identically. They
// are not the same MODULE, and that is what a test mock keys on.
//
// `SecuritySettings.test`, `SettingsTab.test` and `TwoFactorSettings.test` each
// `vi.mock('@clerk/clerk-react/errors')`. Nothing mocks `@clerk/shared/error`.
// So a helper importing from shared/error would slip past those three mocks:
// the predicate the component's error path calls would no longer be the one the
// test replaced, and the tests would keep passing while testing something else.
// Identical behaviour, silently different coverage — which is exactly the shape
// of failure a refactor is supposed to avoid, and it leaves nothing red.
//
// `clerkError.test.ts` pins this: it mocks the module and asserts the mock is
// honoured, so changing this line to `@clerk/shared/error` goes red.
import { isClerkAPIResponseError } from '@clerk/clerk-react/errors';

/**
 * The message to show a person for an error that may have come from Clerk.
 *
 * Clerk's own wording is worth showing where it exists — "we couldn't find that
 * address" tells someone what to do, and a generic fallback is what once made a
 * broken flow indistinguishable from a real failure (SecuritySettings).
 *
 * TWO FALLBACKS, because the call sites genuinely need two. A Clerk error that
 * carries no message means the request reached Clerk and Clerk had nothing to
 * say; anything else — a network failure, a bug — did not get that far, and
 * most sites word those differently ("Reset failed" vs "Couldn't reset your
 * password. Please try again."). Passing one argument keeps the old single
 * fallback behaviour, which is what the two local helpers did.
 */
export function clerkErrorMessage(
  err: unknown,
  clerkFallback: string,
  otherFallback: string = clerkFallback,
): string {
  if (isClerkAPIResponseError(err)) {
    return err.errors[0]?.longMessage || err.errors[0]?.message || clerkFallback;
  }
  return otherFallback;
}

/**
 * Clerk's message about ONE field, or null when none of its errors name it.
 *
 * Clerk tags each error with the parameter it is about (`meta.paramName`), so a
 * refused password can be shown under the password field instead of as a toast
 * about a field the person cannot see (JAR-1702). Read through the same guarded
 * import as clerkErrorMessage, for the same reason given at the top of this file.
 */
export function clerkFieldError(err: unknown, paramName: string): string | null {
  if (!isClerkAPIResponseError(err)) return null;
  const match = err.errors.find((e) => e.meta?.paramName === paramName);
  return match ? match.longMessage || match.message || null : null;
}
